import {
  alpha,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Popper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import { useEffect, useRef, useState, useMemo } from 'react';
import NotificationsActiveTwoToneIcon from '@mui/icons-material/NotificationsActiveTwoTone';
import CorporateFareOutlinedIcon from '@mui/icons-material/CorporateFareOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { styled } from '@mui/material/styles';
import React from 'react';
import { useNavigate } from 'react-router';
import './notifications.css';
import { BaseAPI } from 'src/providers/base-api';
import { useConfirmDialog } from 'src/hooks/useConfirmDialog';
import { useCodeTables } from 'src/hooks/useCodeTables';
import { CODE_TABLE_TYPES, STATUS_CODES } from 'src/types/codeTable';
import dayjs from 'dayjs';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import duration from 'dayjs/plugin/duration';
import { el } from 'date-fns/locale';
import { addDays, format, differenceInCalendarDays, parse, parseISO, startOfDay } from 'date-fns';
import StarBorder from '@mui/icons-material/StarBorder';
import { all } from 'axios';
import { useTenantFeatures } from 'src/hooks/useTenantFeatures';

dayjs.extend(duration);

// Interface for notification items
interface NotificationItem {
  id: string;
  title: string;
  message: string;
  timestamp: Date;
  isRead: boolean;
  type: 'warning' | 'info' | 'success' | 'error';
  link?: string;
  targetRole?: string; // Add this property
  employeeId?: string;
}

const NotificationsBadge = styled(Badge)(
  ({ theme }) => `
    
    .MuiBadge-badge {
        background-color: ${alpha(theme.palette.error.main, 0.1)};
        color: ${theme.palette.error.main};
        min-width: 16px; 
        height: 16px;
        padding: 0;

        &::after {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            right: 10px;
            height: 100%;
            border-radius: 50%;
            box-shadow: 0 0 0 1px ${alpha(theme.palette.error.main, 0.3)};
            content: "";
        }
    }
`
);

function HeaderNotifications() {
  const { confirm, notifySuccess, notifyError, notifyWarning, notifyInfo, Swal } = useConfirmDialog();
  const ref = useRef<any>(null);
  const [isOpen, setOpen] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const tenantFeatures = useTenantFeatures();
  const [category, setCategory] = useState<any>();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [shiftTime, setShiftTime] = useState<any>([]);
  const [employeeSupervisor, setEmployeeSupervisor] = useState('');
  const [quickLinkAnchorEl, setQuickLinkAnchorEl] = useState<null | HTMLElement>(null);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionType, setPermissionType] = useState('Late Coming');
  const [wfhDialogOpen, setWfhDialogOpen] = useState(false);
  const [firstHalf, setFirstHalf] = useState(true);
  const [secondHalf, setSecondHalf] = useState(true);
  const [permissionStart, setPermissionStart] = useState('');
  const [permissionResume, setPermissionResume] = useState('');
  const [permissionEnd, setPermissionEnd] = useState('');
  const [permissionHours, setPermissionHours] = useState('');
  const [permissionReason, setPermissionReason] = useState('');
  const [wfhReason, setWfhReason] = useState('');
  const [emergencyDialogOpen, setEmergencyDialogOpen] = useState(false);
  const [emergencyReason, setEmergencyReason] = useState('');
  const [userLeaveBalancesCount, setUserLeaveBalancesCount] = useState(0);
  const [requestedLeaveCount, setRequestedLeaveCount] = useState(0);
  const [firstHalfEmergency, setFirstHalfEmergency] = useState(false);
  const [secondHalfEmergency, setSecondHalfEmergency] = useState(false);
  const [wfhId, setWfhId] = useState('');
  const [plannedLeaveId, setPlannedLeaveId] = useState('');
  const [sickLeaveId, setSickLeaveId] = useState('');
  const [hasLeaveToday, setHasLeaveToday] = useState(false);

  // Code tables from Redux
  const { getValues } = useCodeTables();
  const leaveStatusValues = getValues(CODE_TABLE_TYPES.LEAVE_STATUS);

  // Get 'Request' status id
  const leaveStatusRequest = useMemo(() => {
    const request = leaveStatusValues?.find((item: any) => item.typeCode === STATUS_CODES.REQUEST);
    return request?.id;
  }, [leaveStatusValues]);
  const [isUpdatePermissionMode, setIsUpdatePermissionMode] = useState(false);
  const [permissionId, setPermissionId] = useState('');
  const [permissionHoursError, setPermissionHoursError] = useState(false);
  const [permissionHoursHelperText, setPermissionHoursHelperText] = useState('');
  const [employeeSupervisorEmail, setEmployeeSupervisorEmail] = useState('');
  const [holidayCalenderList, setHolidayCalenderList] = useState<any>([]);
  const [userLeaveList, setUserLeaveList] = useState<any>([]);
  const [isSandwichLeave, setIsSandwichLeave] = useState(false);
  const [sandwichDatesCount, setSandwichDatesCount] = useState(0);
  const [sandwichLeaveAllocation, setSandwichLeaveAllocation] = useState<any>([]);
  const [partyId, setPartyId] = useState<string | null>(null); // Store partyId consistently
  const [allocationText, setAllocationText] = useState('');
  const [sandwichLeaveDates, setSandwichLeaveDates] = useState<any>([]);
  const [isCrestWorthServer, setIsCrestWorthServer] = useState(false);
  const [isLateLoginFreeze, setIsLateLginFreeze] = useState(false);
  const [generalShiftTiming, setGeneralShiftTiming] = useState<any>(null);

  const [existingHalfStatus, setExistingHalfStatus] = useState<'NONE' | 'FULL' | 'FIRST' | 'SECOND'>('NONE');

  const handleQuickLinkClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setQuickLinkAnchorEl(event.currentTarget);
    getByIdLeaveLists();
    getEmployeeDetail();
  };

  const handleQuickLinkClose = () => {
    setQuickLinkAnchorEl(null);
  };

  const openWFHDialog = () => {
    handleQuickLinkClose();
    sandwichLeaveCalculation(formatDateOnly(new Date()), 1, 7);
    if (existingHalfStatus === 'FULL') {
      Swal.fire({
        icon: 'info',
        title: 'WFH/Leave request already submitted for today!',
        confirmButtonColor: '#4caf50'
      });
      return;
    }

    if (existingHalfStatus === 'FIRST' || existingHalfStatus === 'SECOND') {
      const message =
        existingHalfStatus === 'FIRST'
          ? '1st Half is already applied. 2nd Half has been chosen automatically.'
          : '2nd Half is already applied. 1st Half has been chosen automatically.';

      Swal.fire({
        text: message,
        confirmButtonColor: '#4caf50'
      }).then(() => {
        setWfhReason('');
        setWfhDialogOpen(true);
      });

      return;
    }

    setWfhReason('');
    setWfhDialogOpen(true);
  };
  const openPermissionDialog = () => {
    handleQuickLinkClose();

    // RDS Server validation - check if permission is being applied at least 1 hour before shift start
    if (tenantFeatures.allowSupervisorMarkingAttendance) {
      // Use generalShiftTiming for RDS, not the user's assigned shift
      const rdsShiftTiming = generalShiftTiming;

      if (!rdsShiftTiming) {
        console.warn('⚠️ General shift timing not loaded yet for RDS');
        setPermissionDialogOpen(true);
        setPermissionHoursError(false);
        setPermissionHoursHelperText('');
        setPermissionType('');
        setPermissionStart('');
        setPermissionResume('');
        setPermissionEnd('');
        setPermissionHours('');
        setPermissionReason('');
        return;
      }

      const shiftStart = rdsShiftTiming.startTime || '';
      const shiftEnd = rdsShiftTiming.endTime || '';

      if (shiftStart) {
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const currentTotalMinutes = currentHour * 60 + currentMinute;

        // Parse shift start time
        const [shiftHourStr, shiftMinuteStr] = shiftStart.split(':');
        const shiftStartHour = parseInt(shiftHourStr, 10);
        const shiftStartMinute = parseInt(shiftMinuteStr, 10);
        const shiftStartTotalMinutes = shiftStartHour * 60 + shiftStartMinute;

        // Parse shift end time
        const [endHourStr, endMinuteStr] = shiftEnd.split(':');
        const shiftEndHour = parseInt(endHourStr, 10);
        const shiftEndMinute = parseInt(endMinuteStr, 10);
        const shiftEndTotalMinutes = shiftEndHour * 60 + shiftEndMinute;

        let minutesUntilShift;

        // Detect night shift scenarios:
        // 1. Traditional night shift: ends before it starts (e.g., 22:00 - 06:00)
        // 2. Early morning shift: starts in early hours (0-6 AM) regardless of end time
        const isTraditionalNightShift = shiftEndTotalMinutes < shiftStartTotalMinutes;
        const isEarlyMorningShift = shiftStartHour >= 0 && shiftStartHour < 6;

        if (isEarlyMorningShift && !isTraditionalNightShift) {
          // Early morning shift (e.g., 01:33 - 07:32)
          // If current time is after noon (12:00 PM), it's the evening before the shift
          if (currentTotalMinutes >= 720) {
            // 720 = 12:00 PM
            // Calculate time until shift starts (next day)
            // Minutes from now until midnight + minutes from midnight to shift start
            const minutesUntilMidnight = 24 * 60 - currentTotalMinutes;
            minutesUntilShift = minutesUntilMidnight + shiftStartTotalMinutes;
          } else if (currentTotalMinutes < shiftStartTotalMinutes) {
            // Current time is before shift start (same day, early morning)
            minutesUntilShift = shiftStartTotalMinutes - currentTotalMinutes;
          } else if (currentTotalMinutes >= shiftStartTotalMinutes && currentTotalMinutes < shiftEndTotalMinutes) {
            // Currently within shift time
            minutesUntilShift = -(currentTotalMinutes - shiftStartTotalMinutes);
          } else {
            // After shift end but before noon (shouldn't apply for next day shift from here)
            minutesUntilShift = -1; // Mark as past shift time
          }
        } else if (isTraditionalNightShift) {
          // Traditional night shift that crosses midnight (e.g., 21:30 - 06:30)
          if (currentTotalMinutes >= shiftStartTotalMinutes) {
            // Current time is after shift start on same day
            minutesUntilShift = -(currentTotalMinutes - shiftStartTotalMinutes);
          } else if (currentTotalMinutes < shiftEndTotalMinutes) {
            // Current time is before shift end on next day (within shift)
            minutesUntilShift = -(24 * 60 - shiftStartTotalMinutes + currentTotalMinutes);
          } else {
            // Current time is between shift end and shift start (daytime)
            minutesUntilShift = shiftStartTotalMinutes - currentTotalMinutes;
          }
        } else {
          // Regular day shift (same day, e.g., 06:00 - 15:00, 13:00 - 22:00)
          minutesUntilShift = shiftStartTotalMinutes - currentTotalMinutes;
        }

        // Block if current time is AT or AFTER shift start time
        if (minutesUntilShift <= 0) {
          Swal.fire({
            icon: 'warning',
            title: 'Permission Request Not Allowed',
            text: 'Permission cannot be applied after shift start time.',
            confirmButtonColor: '#4caf50'
          });
          return;
        }

        // Block if less than 60 minutes before shift start
        if (minutesUntilShift < 60) {
          const remainingMinutes = 60 - minutesUntilShift;
          Swal.fire({
            icon: 'warning',
            title: 'Permission Request Not Allowed',
            text: `Permission must be applied at least 1 hour before shift start time. Please wait ${remainingMinutes} more minute(s).`,
            confirmButtonColor: '#4caf50'
          });
          return;
        }
      }
    }

    setPermissionDialogOpen(true);
    setPermissionHoursError(false);
    setPermissionHoursHelperText('');
    setPermissionType('');
    setPermissionStart('');
    setPermissionResume('');
    setPermissionEnd('');
    setPermissionHours('');
    setPermissionReason('');
  };

  const OpenEmergencyDialog = () => {
    handleQuickLinkClose();
    sandwichLeaveCalculation(formatDateOnly(new Date()), 1, 1);
    if (existingHalfStatus === 'FULL') {
      Swal.fire({
        icon: 'info',
        title: 'Leave request already submitted for today !',
        confirmButtonColor: '#4caf50'
      });

      return;
    }

    if (existingHalfStatus === 'FIRST' || existingHalfStatus === 'SECOND') {
      const message =
        existingHalfStatus === 'FIRST'
          ? '1st Half is already applied. 2nd Half has been chosen automatically.'
          : '2nd Half is already applied. 1st Half has been chosen automatically.';

      Swal.fire({
        text: message,
        confirmButtonColor: '#4caf50'
      }).then(() => {
        setEmergencyDialogOpen(true);
        setEmergencyReason('');
      });

      return;
    }
    setEmergencyDialogOpen(true);
    setEmergencyReason('');
  };

  const formatTime = (time) => (time ? dayjs(`1970-01-01T${time}`).format('hh:mm A') : '');

  const MAX_PERMISSION_MINUTES = 120;

  useEffect(() => {
    if (!permissionStart || !permissionEnd) {
      resetPermissionState();
      return;
    }

    let start = dayjs(`2023-01-01T${permissionStart}`);
    let end = dayjs(`2023-01-01T${permissionEnd}`);

    if (!end.isAfter(start)) {
      end = end.add(1, 'day');
    }

    const diffInMinutes = end.diff(start, 'minute');
    const hours = Math.floor(diffInMinutes / 60);
    const minutes = diffInMinutes % 60;

    const formatted = `${hours > 0 ? `${hours} hr ` : ''}${minutes > 0 ? `${minutes} min` : ''}`.trim();
    setPermissionHours(formatted);

    if (diffInMinutes > MAX_PERMISSION_MINUTES && permissionType !== 'Continue WFH') {
      setPermissionHoursError(true);
      setPermissionHoursHelperText('2hr Max');
    } else {
      setPermissionHoursError(false);
      setPermissionHoursHelperText('');
    }
  }, [permissionStart, permissionEnd]);

  const resetPermissionState = () => {
    setPermissionHours('');
    setPermissionHoursError(false);
    setPermissionHoursHelperText('');
  };

  useEffect(() => {
    if (!shiftTime.length) return;

    const shiftStart = shiftTime[0]?.startTime || '';
    const shiftEnd = shiftTime[0]?.endTime || '';
    const formattedStart = formatTime(permissionStart);
    const formattedEnd = formatTime(permissionEnd);
    setIsLateLginFreeze(false);

    if (permissionType === 'Late Coming') {
      // For RDS server, don't check if current time >= shift start
      // because they apply permission the night before
      if (tenantFeatures.allowSupervisorMarkingAttendance) {
        const time = new Date();
        const formatted = time.toTimeString().slice(0, 5);
        if (formatted >= shiftStart) {
          setIsLateLginFreeze(true);
        }
      }

      setPermissionStart(shiftStart);

      if (permissionEnd) {
        setPermissionReason(`I will reach office at ${formattedEnd}`);
      } else {
        setPermissionReason('');
      }
    }

    if (permissionType === 'Leaving Early') {
      setIsLateLginFreeze(false);
      setPermissionEnd(shiftEnd);

      if (permissionStart) {
        setPermissionReason(`Unavailable from ${formattedStart}`);
      } else {
        setPermissionReason('');
      }
    }

    if (permissionType === 'Permission') {
      setIsLateLginFreeze(false);
    }

    if (permissionType === 'Continue WFH') {
      setIsLateLginFreeze(false);
      if (permissionResume) {
        setPermissionReason(`Will continue my work from home`);
      } else {
        setPermissionReason('');
      }
    }
  }, [
    permissionType,
    shiftTime,
    permissionEnd,
    permissionStart,
    permissionHoursError,
    isLateLoginFreeze,
    permissionResume,
    tenantFeatures
  ]);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const navigate = useNavigate();

  const OrganisationProfile = () => {
    navigate('/organisationprofile');
    setAnchorEl(null);
  };

  const WorkLocations = () => {
    navigate('/work-locations/list');
    setAnchorEl(null);
  };

  const Departments = () => {
    navigate('/department/list');
    setAnchorEl(null);
  };

  const Designation = () => {
    navigate('/designation/list');
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const getEmployeeDetail = async () => {
    try {
      const userPartyId = JSON.parse(sessionStorage.getItem('UserpartyId'));
      if (!userPartyId) {
        console.warn('UserpartyId not found in session storage');
        return;
      }

      await BaseAPI.getEmployeeByPartyID(userPartyId, async (result: any) => {
        if (result.status !== 200) {
          console.warn('Failed to fetch personal information');
          return;
        }

        const employeeData = result.data;
        if (!employeeData) {
          console.warn('Employee data not found for the given party ID');
          return;
        }

        const employeeDepartment = employeeData?.department;
        const employeeId = employeeData?.id;
        const employeeSupervisor = employeeData?.immediateSupervisor;
        const employeeSupervisorID = employeeData?.immediateSupervisorPartyId;

        if (employeeSupervisorID) {
          await BaseAPI.getEmployeeByPartyID(parseInt(employeeSupervisorID), (empResponse) => {
            if (empResponse.status === 200) {
              const employeeId = empResponse?.data;
              setEmployeeSupervisorEmail(employeeId?.email);
            } else {
              console.error('Could not find employee ID from party ID:', empResponse);
            }
          });
        }

        setEmployeeSupervisor(employeeSupervisor);

        let employeeShiftType = 'G';

        if (employeeDepartment === 'Netops') {
          const today = new Date().toISOString().split('T')[0];

          const response = await BaseAPI.getShiftPlanByEmployeeId(employeeId);
          if (response.status === 200 && Array.isArray(response.data)) {
            const todayShift = response.data?.find((shift) => shift.startData === today);
            if (todayShift) {
              employeeShiftType = todayShift.shiftType;
            } else {
              console.error('No shift found for today');
            }
          } else {
            console.error('Failed to fetch shift plan');
          }
        }

        await BaseAPI.getShiftTime((shiftResult: any) => {
          if (shiftResult.status === 200 && Array.isArray(shiftResult.data)) {
            const shiftTime = shiftResult.data.filter((data) => data.shiftType === employeeShiftType);

            setShiftTime(shiftTime);
          } else {
            console.warn('Failed to fetch shift timing data');
          }
        });
      });
    } catch (error) {
      console.error('Error fetching employee detail:', error);
    }
  };

  const fetchGeneralShiftTiming = async () => {
    try {
      await BaseAPI.getShiftTime((shiftResult: any) => {
        if (shiftResult.status === 200 && Array.isArray(shiftResult.data)) {
          const generalShift = shiftResult.data.find((data) => data.shiftType === 'G');
          if (generalShift) {
            setGeneralShiftTiming(generalShift);
          } else {
            console.warn('⚠️ General shift (G) not found in shift timings');
          }
        } else {
          console.warn('Failed to fetch shift timing data for RDS');
        }
      });
    } catch (error) {
      console.error('Error fetching general shift timing for RDS:', error);
    }
  };

  const getPermissioByEmployeeIdData = async (selectedType) => {
    const today = new Date();
    const formattedToday = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;

    const userPartyId = JSON.parse(sessionStorage.getItem('UserpartyId'));

    try {
      const response = await BaseAPI.getPermissioByEmployeeId(userPartyId);

      if (response.status === 200 && Array.isArray(response.data)) {
        const matchedPermission = response.data.find(
          (permission) => permission.date === formattedToday && permission.permissionType === selectedType
        );
        return matchedPermission || null;
      } else {
        console.warn('Failed to fetch permission');
        return null;
      }
    } catch (error) {
      console.error('Error fetching employee permission data:', error);
      return null;
    }
  };

  const formatDateOnly = (d: Date) => format(d, 'yyyy-MM-dd');

  const getByIdLeaveLists = async () => {
    const userDetail = JSON.parse(sessionStorage.getItem('UserDetail') || '{}');
    const partyId = userDetail.partyId;

    try {
      await BaseAPI.getLeaveDataByUser(partyId, (result: any) => {
        if (result.status === 200) {
          const userData = result?.data;

          let statusForToday: 'NONE' | 'FULL' | 'FIRST' | 'SECOND' = 'NONE';

          const todayLeaves = userData.filter(
            (row: any) =>
              [STATUS_CODES.REQUEST, STATUS_CODES.APPROVED, STATUS_CODES.AVAILED, STATUS_CODES.CANCEL_REQUEST].includes(
                row.status?.typeCode
              ) && dayjs(row.startDate).isSame(dayjs(), 'day')
          );
          const leaveList = userData.filter((row: any) =>
            [STATUS_CODES.REQUEST, STATUS_CODES.APPROVED, STATUS_CODES.AVAILED, STATUS_CODES.CANCEL_REQUEST].includes(row.status?.typeCode)
          );
          setUserLeaveList(leaveList);

          const conflict = leaveList.filter((item) => {
            const start = formatDateOnly(new Date(item.startDate));
            const end = formatDateOnly(new Date(item.endDate));
            const selected = formatDateOnly(new Date());

            if (!(selected <= end && selected >= start)) return false;
            const overlapStart = selected > start ? selected : start;
            const overlapEnd = selected < end ? selected : end;

            const cancelledDates = item.cancelledDates || [];
            let current = new Date(overlapStart);
            while (formatDateOnly(current) <= overlapEnd) {
              const dateStr = formatDateOnly(current);

              if (cancelledDates.includes(dateStr)) return false;

              current.setDate(current.getDate() + 1);
            }

            return true;
          });

          if (conflict.length > 0) {
            let firstHalfTaken = false;
            let secondHalfTaken = false;

            conflict.forEach((leave: any) => {
              // {1,0} -> fullday
              // {2,0} -> firstHalf
              //{3,0} -> secondHalf
              //{1,1} -> multiple full day
              // {1,2} -> muliple 1st half
              const start = formatDateOnly(new Date(leave.startDate));
              const end = formatDateOnly(new Date(leave.endDate));
              const selected = formatDateOnly(new Date());
              if ((start == end && start == selected) || (selected !== start && selected !== end)) {
                if (Array.isArray(leave.isHalfDay)) {
                  const [first, second] = leave.isHalfDay;
                  if (first === 1 || first === 2) firstHalfTaken = true;
                  if (first === 1 || first === 3) secondHalfTaken = true;
                }
              } else {
                if (Array.isArray(leave.isHalfDay)) {
                  const [first, second] = leave.isHalfDay;
                  if (second === 1 || second === 2) firstHalfTaken = true;
                  if (second === 1 || second === 3) secondHalfTaken = true;
                }
              }
            });

            if (firstHalfTaken && secondHalfTaken) {
              statusForToday = 'FULL';
            } else if (firstHalfTaken) {
              statusForToday = 'FIRST';
            } else if (secondHalfTaken) {
              statusForToday = 'SECOND';
            }
          }
          setExistingHalfStatus(statusForToday);
        } else {
          console.error(result.message);
        }
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpen = (): void => {
    setOpen(true);
    // Mark all notifications as read when opening the notification panel
    if (notifications.length > 0) {
      const updatedNotifications = notifications.map((notification) => ({
        ...notification,
        isRead: true
      }));
      setNotifications(updatedNotifications);
      sessionStorage.setItem('notifications', JSON.stringify(updatedNotifications));
      setUnreadCount(0);
    }
  };

  const navigateToNotificationPage = () => {
    navigate('/notifications');
    handleClose();
  };

  const handleNotificationClick = (notification: NotificationItem) => {
    // Mark the notification as read
    const updatedNotifications = notifications.map((item) => (item.id === notification.id ? { ...item, isRead: true } : item));

    setNotifications(updatedNotifications);
    sessionStorage.setItem('notifications', JSON.stringify(updatedNotifications));

    // Calculate new unread count
    const newUnreadCount = updatedNotifications.filter((item) => !item.isRead).length;
    setUnreadCount(newUnreadCount);

    // Navigate to the link if provided
    if (notification.link) {
      navigate(notification.link);
      handleClose();
    }
  };

  const getSideBarDate = async () => {
    const dataSidebar = JSON.parse(sessionStorage.getItem('operations') || '[]');
    dataSidebar.map((dataValue: any) => {
      switch (dataValue.refName) {
        case 'Settings': {
          setCategory(dataValue?.refName);
          break;
        }

        default: {
          ('No  Value Added');
          break;
        }
      }
    });
  };

  // Function to check if user has a specific role
  const checkUserHasRole = (roleName) => {
    const userData = JSON.parse(sessionStorage.getItem('UserDetail') || '{}');

    if (!userData || !userData.roles) {
      console.error('No user data or roles found');
      return false;
    }

    // Check if user has the specified role
    const hasRole = userData.roles.some((role) => role.name === roleName || role.refName === roleName);
    return hasRole;
  };

  // Function to check asset availability notifications
  const checkAvailableAssets = (assets) => {
    const availableAssets = assets.filter((asset) => asset.assetStatus === 'Available');
    const count = availableAssets.length;

    const isLow = count <= 3;

    // Create notification if assets are low
    if (isLow) {
      createLowAssetNotification(count);
    }
  };

  // Function to create low asset notification
  const createLowAssetNotification = (count) => {
    const today = new Date().toDateString();

    // Create notification object
    const newNotification = {
      id: 'asset-' + Date.now(),
      title: 'Low Asset Availability Alert',
      message: `There are only ${count} assets with "Available" status. Consider procuring more assets.`,
      timestamp: new Date(),
      isRead: false,
      type: 'warning',
      link: '/assetdetails/list',
      targetRole: 'Asset Management'
    };

    // Add to notifications
    createRoleSpecificNotification(newNotification, 'Asset Management');
  };

  // Function to check warranty expirations
  const checkWarrantyExpirations = (assets) => {
    const today = new Date();
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(today.getDate() + 3);

    // Filter assets with warranties expiring in the next 3 days
    const expiringWarranties = assets.filter((asset) => {
      if (!asset.warrantyEndDate) {
        return false;
      }

      // Ensure warrantyEndDate is a Date object
      const warrantyEnd = new Date(asset.warrantyEndDate);

      // Check if the warranty is expiring in the next 3 days
      const isExpiring = warrantyEnd >= today && warrantyEnd <= threeDaysFromNow;
      if (isExpiring) {
        console.error(`Asset ${asset.assetId} has warranty expiring soon!`);
      }
      return isExpiring;
    });

    // Create notifications for each expiring warranty
    if (expiringWarranties.length > 0) {
      expiringWarranties.forEach((asset) => {
        createWarrantyExpirationNotification(asset);
      });
    }
  };

  // Function to create warranty expiration notification
  const createWarrantyExpirationNotification = (asset) => {
    const warrantyDate = new Date(asset.warrantyEndDate);
    const formattedDate = `${warrantyDate.getDate().toString().padStart(2, '0')}-${(warrantyDate.getMonth() + 1).toString().padStart(2, '0')}-${warrantyDate.getFullYear()}`;

    const newNotification = {
      id: 'warranty-' + Date.now() + '-' + asset.assetId,
      title: 'Warranty Expiring Soon',
      message: `Warranty for asset ${asset.assetId} (${asset.assetType} - ${asset.make}) is expiring on ${formattedDate}. Consider renewal.`,
      timestamp: new Date(),
      isRead: false,
      type: 'warning',
      link: `/assetdetails/${asset.id}?mode=view`,
      targetRole: 'Asset Management'
    };

    // Create role-specific notification
    createRoleSpecificNotification(newNotification, 'Asset Management');
  };

  // Function to create role-specific notification
  const createRoleSpecificNotification = (notification, targetRole) => {
    // Get existing notifications from sessionStorage
    const storedNotifications = sessionStorage.getItem('notifications');
    const existingNotifications = storedNotifications ? JSON.parse(storedNotifications) : [];

    // Add the target role to the notification
    const roleSpecificNotification = {
      ...notification,
      targetRole: targetRole
    };

    // Check if notification with same ID already exists
    const notificationExists = existingNotifications.some((item) => item.id === roleSpecificNotification.id);

    if (!notificationExists) {
      // Add to beginning of array
      existingNotifications.unshift(roleSpecificNotification);

      // Store back in sessionStorage
      sessionStorage.setItem('notifications', JSON.stringify(existingNotifications));

      // Update local state
      checkAssetAvailabilityNotifications();
    }
  };

  // Add this to the checkAssetAvailabilityNotifications function in index.tsx
  // to filter notifications by employee

  // In index.tsx - Update the checkAssetAvailabilityNotifications function:
  const checkAssetAvailabilityNotifications = () => {
    // Get notifications from sessionStorage or initialize empty array
    const storedNotifications = sessionStorage.getItem('notifications');
    const parsedNotifications: NotificationItem[] = storedNotifications ? JSON.parse(storedNotifications) : [];

    // Get current employee ID
    const userData = JSON.parse(sessionStorage.getItem('UserDetail') || '{}');
    const currentEmployeeId = userData?.employeeId;

    // Filter notifications based on role and employee ID
    const userNotifications = parsedNotifications.filter((notification) => {
      // If notification has employeeId, only show to that employee
      if (notification.employeeId) {
        return notification.employeeId === currentEmployeeId;
      }

      // Otherwise, use role-based filtering
      if (!notification.targetRole) return true;
      return checkUserHasRole(notification.targetRole);
    });

    setNotifications(userNotifications);

    // Count unread notifications
    const unread = userNotifications.filter((notification) => !notification.isRead).length;
    setUnreadCount(unread);
  };
  // Function to fetch asset data and generate notifications
  const fetchAssetsAndGenerateNotifications = async () => {
    try {
      const result = await new Promise((resolve, reject) => {
        BaseAPI.getAllAssetDetails((response: any) => {
          if (response.status === 200) {
            resolve(response.data);

            // Check for available assets count
            checkAvailableAssets(response.data);

            // Check for warranty expirations
            checkWarrantyExpirations(response.data);
          } else {
            reject(new Error(response.message || 'Failed to fetch asset details'));
          }
        });
      });
    } catch (error) {
      console.error('Error fetching asset details for notifications:', error);
    }
  };

  useEffect(() => {
    if (Boolean(quickLinkAnchorEl)) {
      getByIdLeaveLists();
      getSideBarDate();
      getLeaveTypes();
      getEmployeeDetail();
      getAllHolidays();
      const userDetail = JSON.parse(sessionStorage.getItem('UserDetail'));
      const data = JSON.parse(sessionStorage.getItem('UserpartyId'));
      const userPartyId = data || userDetail?.partyId;
      setPartyId(userPartyId);
    }
  }, [quickLinkAnchorEl]);

  // Fetch general shift timing when tenant config loads (for RDS 1-hour-before-shift validation)
  useEffect(() => {
    if (tenantFeatures.allowSupervisorMarkingAttendance) {
      fetchGeneralShiftTiming();
    }
  }, [tenantFeatures.allowSupervisorMarkingAttendance]);

  // Function to format date
  const formatNotificationDate = (date: Date) => {
    const now = new Date();
    const notificationDate = new Date(date);

    // If it's today
    if (now.toDateString() === notificationDate.toDateString()) {
      return notificationDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    // If it's in the last week
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    if (notificationDate > weekAgo) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return days[notificationDate.getDay()];
    }

    // Otherwise, show the date
    return notificationDate.toLocaleDateString();
  };

  const fetchUserLeaveBalance = async (leaveType: string) => {
    const userDetail = JSON.parse(sessionStorage.getItem('UserDetail') || '{}');
    const partyId = userDetail?.partyId?.toString() || '';
    try {
      const leaveBalancePromise = new Promise<void>((resolve) => {
        BaseAPI.getUserLeaveBalancesByLeaveType(Number(partyId), leaveType, (response) => {
          if (response?.data?.leaveBalance !== undefined) {
            const balance = parseFloat(parseFloat(response.data.leaveBalance).toFixed(1));
            setUserLeaveBalancesCount(balance);
          } else {
            setUserLeaveBalancesCount(0);
          }
          resolve();
        });
      });

      const requestedLeavePromise = new Promise<void>((resolve) => {
        BaseAPI.getRequestedLeaveBalance(Number(partyId), Number(leaveType), (response) => {
          if (response?.data?.totalRequestlLeaves !== undefined) {
            const requestedLeaves = parseFloat(parseFloat(response.data.totalRequestlLeaves).toFixed(1));
            setRequestedLeaveCount(requestedLeaves);
          } else {
            setRequestedLeaveCount(0);
          }
          resolve();
        });
      });

      await Promise.all([leaveBalancePromise, requestedLeavePromise]);
    } catch (error) {
      console.error('Error in fetchUserLeaveBalance:', error);
    }
  };
  const getLeaveTypes = async () => {
    try {
      await BaseAPI.getLeaveTypeListData((result) => {
        if (result.status === 200) {
          const workFromHomeId = result.data.filter((data) => data.leaveName === 'Work From Home');
          const plannedLeaveId = result.data.filter((data) => data.leaveName === 'Planned Leave');
          const sickLeaveId = result.data.filter((data) => data.leaveName === 'Sick Leave');
          setWfhId(workFromHomeId[0]?.id);
          setPlannedLeaveId(plannedLeaveId[0]?.id);
          setSickLeaveId(sickLeaveId[0]?.id);
        } else {
          console.error(result.message);
        }
      });
    } catch (e) {
      console.error(e);
    }
  };
  const submitWFHRequest = async () => {
    setWfhDialogOpen(false);
    const userDetail = JSON.parse(sessionStorage.getItem('UserDetail') || '{}');
    const userEmaildata = JSON.parse(sessionStorage.getItem('UserEmail'));

    const partyId = userDetail?.partyId?.toString() || '';
    const today = new Date().toISOString().split('T')[0];

    if (!firstHalf && !secondHalf) {
      await Swal.fire({
        icon: 'warning',
        title: 'Invalid Selection',
        text: 'Please select at least one half.',
        confirmButtonColor: '#f44336'
      });
      return;
    }

    const applyingFirstHalf = existingHalfStatus !== 'FIRST' && existingHalfStatus !== 'FULL' && firstHalf;
    const applyingSecondHalf = existingHalfStatus !== 'SECOND' && existingHalfStatus !== 'FULL' && secondHalf;

    const wfhTypeText =
      applyingFirstHalf && applyingSecondHalf ? 'FULL DAY' : applyingFirstHalf ? '1ST HALF' : applyingSecondHalf ? '2ND HALF' : '';

    try {
      const confirm = await Swal.fire({
        title: 'Confirm WFH Request',
        html: `You are requesting WFH for <strong>today</strong> as <strong>${wfhTypeText}</strong> due to <strong>${wfhReason}</strong> ?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#4caf50',
        cancelButtonColor: '#35cce6',
        confirmButtonText: 'Apply',
        reverseButtons: true
      });

      if (!confirm.isConfirmed) return;

      // Calculate isHalfDay based on actual applying halves
      const isHalfDay =
        applyingFirstHalf && applyingSecondHalf
          ? ['1', '0'] // Full day
          : applyingFirstHalf
            ? ['2', '0'] // First half only
            : ['3', '0']; // Second half only

      const firstDay = applyingFirstHalf && applyingSecondHalf ? '1' : applyingFirstHalf ? '2' : '3';

      let daysDifference = 0;
      if (applyingFirstHalf) daysDifference += 0.5;
      if (applyingSecondHalf) daysDifference += 0.5;
      const LeaveTypes = {
        leaveType: wfhId,
        startDate: today,
        endDate: today,
        totalDays: daysDifference,
        comments: wfhReason,
        status: leaveStatusRequest,
        party: partyId,
        isUnplanned: false,
        isHalfDay,
        firstDay,
        secondDay: 0,
        isRequestedLeave: true,
        isQuickLinkLeave: true,
        email: userEmaildata,
        supervisorEmailId: employeeSupervisorEmail
      };

      await BaseAPI.leaveCreate(LeaveTypes, async (result) => {
        if (result.data) {
          await BaseAPI.getEmployeeByPartyID(parseInt(partyId), async (empResponse) => {
            if (empResponse && empResponse.data) {
              const employeeId = empResponse.data.id;

              await BaseAPI.updateRequestedLeave(employeeId, Number(wfhId), daysDifference, true, async (responseData) => {
                await Swal.fire({
                  icon: 'success',
                  title: 'WFH Request Submitted',
                  text: 'Your request has been successfully submitted.',
                  confirmButtonColor: '#4caf50'
                });

                setWfhDialogOpen(false);
                setWfhReason('');
                setFirstHalf(true);
                setSecondHalf(true);
                await getByIdLeaveLists();
              });
            } else {
              console.warn('Employee data not found for party ID:', partyId);
            }
          });
        } else {
          throw new Error(result.message || 'WFH request submission failed.');
        }
      });
    } catch (error) {
      console.error('WFH Submission Error:', error);
      await Swal.fire('Error', 'Something went wrong. Please try again later.', 'error');
    }
  };

  const submitEmergencyLeaveRequest = async () => {
    setEmergencyDialogOpen(false);
    const userDetail = JSON.parse(sessionStorage.getItem('UserDetail') || '{}');
    const partyId = userDetail?.partyId?.toString() || '';
    const userEmaildata = JSON.parse(sessionStorage.getItem('UserEmail'));

    const today = formatDateOnly(new Date());

    if (!firstHalfEmergency && !secondHalfEmergency) {
      await Swal.fire({
        icon: 'warning',
        title: 'Invalid Selection',
        text: 'Please select at least one half.',
        confirmButtonColor: '#f44336'
      });
      return;
    }
    const applyingFirstHalf = existingHalfStatus !== 'FIRST' && existingHalfStatus !== 'FULL' && firstHalfEmergency;
    const applyingSecondHalf = existingHalfStatus !== 'SECOND' && existingHalfStatus !== 'FULL' && secondHalfEmergency;
    const emergencyLeaveTypeText =
      applyingFirstHalf && applyingSecondHalf ? 'FULL DAY' : applyingFirstHalf ? '1ST HALF' : applyingSecondHalf ? '2ND HALF' : '';

    try {
      const { isConfirmed } = await Swal.fire({
        title: 'Confirm Request',
        html: `Are you sure you want to apply for <strong>${emergencyLeaveTypeText}  </strong> leave on <strong>today</strong>?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#4caf50',
        cancelButtonColor: '#35cce6',
        confirmButtonText: 'Apply',
        reverseButtons: true
      });

      if (!isConfirmed) return;

      const isHalfDay = applyingFirstHalf && applyingSecondHalf ? ['1', '0'] : applyingFirstHalf ? ['2', '0'] : ['3', '0'];

      const firstDay = applyingFirstHalf && applyingSecondHalf ? '1' : applyingFirstHalf ? '2' : '3';

      let daysDifference = 0;

      if (applyingFirstHalf) daysDifference += 0.5;
      if (applyingSecondHalf) daysDifference += 0.5;

      // const leaveTypeId =emergencyReason.includes('I am not well ')? sickLeaveId : plannedLeaveId

      const leaveTypeId = sickLeaveId;
      // const sandwichLeaveData = calculateSandwichLeaveForToday(today);
      const sandwichLeaveData = sandwichLeaveCalculation(today, daysDifference, 1);
      const allocationDays = Number(daysDifference + sandwichLeaveData.sandwichCount);
      const leaveAllocation = [{ type: 1, days: allocationDays }];

      let LeaveTypes = {
        leaveType: leaveTypeId,
        startDate: today,
        endDate: today,
        totalDays: sandwichLeaveData.sandwichCount ? daysDifference + sandwichLeaveData.sandwichCount : daysDifference,
        comments: emergencyReason,
        status: leaveStatusRequest,
        party: partyId,
        isUnplanned: false,
        isHalfDay,
        firstDay,
        secondDay: 0,
        isRequestedLeave: true,
        isQuickLinkLeave: true,
        email: userEmaildata,
        supervisorEmailId: employeeSupervisorEmail,
        isSandwichLeave: sandwichLeaveData.isSandwich ? sandwichLeaveData.isSandwich : false,
        ackSandwichLeave: sandwichLeaveData.isSandwich ? sandwichLeaveData.isSandwich : false,
        sandwichLeaveCount: sandwichLeaveData.sandwichCount ? sandwichLeaveData.sandwichCount : 0,
        sandwichLeaveAllocation: sandwichLeaveData.isSandwich ? leaveAllocation : [],
        sandwichLeaveDates: sandwichLeaveData.isSandwich ? sandwichLeaveDates : []
      };

      await BaseAPI.leaveCreate(LeaveTypes, async (result) => {
        if (result.data) {
          await BaseAPI.getEmployeeByPartyID(parseInt(partyId), async (empResponse) => {
            if (empResponse && empResponse.data) {
              const employeeId = empResponse.data.id;

              await BaseAPI.updateRequestedLeave(employeeId, Number(leaveTypeId), daysDifference, true, async (responseData) => {
                await Swal.fire({
                  icon: 'success',
                  title: 'Request Submitted',
                  text: 'Your request has been successfully submitted.',
                  confirmButtonColor: '#4caf50'
                });

                setEmergencyDialogOpen(false);
                setEmergencyReason('');
                await getByIdLeaveLists();
              });
            } else {
              console.warn('Employee data not found for party ID:', partyId);
            }
          });
        } else {
          throw new Error(result.message || 'Emergency leave  request submission failed.');
        }
      });
    } catch (error) {
      console.error('Emergency leave Submission Error:', error);
      await Swal.fire('Error', 'Something went wrong. Please try again later.', 'error');
    }
  };

  const getAllHolidays = async () => {
    try {
      await BaseAPI.getAllHolidays((result: any) => {
        if (result.status === 200) {
          setHolidayCalenderList(result.data[0].holidays);
        } else {
          console.error(result.message);
        }
      });
    } catch (error) {
      console.error('Failed to fetch all leave data:', error);
    }
  };

  const sandwichLeaveCalculation = (appliedDate: string, daysDifference: number, leaveType: number) => {
    try {
      const startDate = parse(appliedDate, 'yyyy-MM-dd', new Date());
      const endDate = parse(appliedDate, 'yyyy-MM-dd', new Date());
      const formatDateOnly = (d: Date) => format(d, 'yyyy-MM-dd');

      const isWeekend = (d: Date) => [0, 6].includes(d.getDay());
      const isHoliday = (d: Date) => (holidayCalenderList || []).some((h) => h?.date === formatDateOnly(d));

      const checkLeaveAppliedOn = (d: Date) =>
        (userLeaveList || []).some(
          (l) => l?.startDate && l?.leaveType?.id !== 7 && formatDateOnly(new Date(l?.startDate)) === formatDateOnly(d)
        );

      const totalDays = differenceInCalendarDays(endDate, startDate) + 1;

      // sandwich start
      let datesInRange = Array.from({ length: totalDays }, (_, i) => addDays(startDate, i));
      let sandwichInsideDates: string[] = [];
      let sandwichOutsideDates: string[] = [];
      let isSandwich = false;
      let sandwichCount = 0;
      let allDates = [];
      if (leaveType !== 7) {
        for (let i = 1; i < datesInRange.length - 1; i++) {
          const d = datesInRange[i];
          if (isWeekend(d) || isHoliday(d)) sandwichInsideDates.push(formatDateOnly(d));
        }
        let backward = addDays(startDate, -1);
        while (true) {
          if (checkLeaveAppliedOn(backward)) break;
          if (isWeekend(backward) || isHoliday(backward)) {
            sandwichOutsideDates.push(formatDateOnly(backward));
            backward = addDays(backward, -1);
          } else break;
        }
        if (!checkLeaveAppliedOn(backward)) sandwichOutsideDates.length = 0;
        let forward = addDays(endDate, 1);
        const forwardTemp: string[] = [];
        while (true) {
          if (checkLeaveAppliedOn(forward)) break;
          if (isWeekend(forward) || isHoliday(forward)) {
            forwardTemp.push(formatDateOnly(forward));
            forward = addDays(forward, 1);
          } else break;
        }
        if (checkLeaveAppliedOn(forward)) sandwichOutsideDates.push(...forwardTemp);

        const connectedAppliedDates: string[] = [];
        let backwardDate = addDays(startDate, -1);
        while (checkLeaveAppliedOn(backwardDate) || isWeekend(backwardDate) || isHoliday(backwardDate)) {
          if (checkLeaveAppliedOn(backwardDate)) connectedAppliedDates.push(formatDateOnly(backwardDate));
          backwardDate = addDays(backwardDate, -1);
          if (!checkLeaveAppliedOn(backwardDate) && !isWeekend(backwardDate) && !isHoliday(backwardDate)) break;
        }

        let forwardDate = addDays(endDate, 1);
        while (checkLeaveAppliedOn(forwardDate) || isWeekend(forwardDate) || isHoliday(forwardDate)) {
          if (checkLeaveAppliedOn(forwardDate)) connectedAppliedDates.push(formatDateOnly(forwardDate));
          forwardDate = addDays(forwardDate, 1);
          if (!checkLeaveAppliedOn(forwardDate) && !isWeekend(forwardDate) && !isHoliday(forwardDate)) break;
        }

        allDates = [
          ...datesInRange.map((d) => formatDateOnly(d)),
          ...sandwichInsideDates,
          ...sandwichOutsideDates,
          ...connectedAppliedDates
        ]
          .sort()
          .filter((v, i, a) => a.indexOf(v) === i);

        sandwichCount = sandwichInsideDates.length + sandwichOutsideDates.length;
        isSandwich = sandwichCount > 0;

        setIsSandwichLeave(isSandwich);
        setSandwichDatesCount(sandwichCount);
        if (isSandwich) {
          setSandwichLeaveDates(allDates);
        }
      }
      // sandwich end

      daysDifference += sandwichOutsideDates.length;
      daysDifference = parseFloat(daysDifference.toFixed(1));
      checkLeaveBalance(1, daysDifference, isSandwich);
      return { isSandwich, sandwichCount };
    } catch (error) {
      console.error('Error in day calculation:', error);
      // return [];
    }
  };

  // Updated checkLeaveBalance function with better error handling
  const getRequestedLeaveBalanceByLeaveType = async (partyId, leaveType) => {
    let pendingLeaves;
    await BaseAPI.getRequestedLeaveBalance(Number(partyId), leaveType, (requestedLeaveResponse) => {
      if (!requestedLeaveResponse || !requestedLeaveResponse.data) {
        console.error('Failed to get requested leave data');
        return;
      }
      pendingLeaves = parseFloat(parseFloat(requestedLeaveResponse.data.totalRequestlLeaves || 0).toFixed(1));
    });
    return pendingLeaves;
  };

  const checkLeaveBalance = async (leaveType, daysDifference, isSandwich) => {
    if (!leaveType || !daysDifference || !partyId) return;

    const daysRequested = parseFloat(parseFloat(daysDifference).toFixed(1));

    // sandwich leave borrow
    if (isSandwich) {
      let userPlannedLeaveBalance = 0;
      let userSickLeaveBalance = 0;
      let availPlannedLeaveBalance = 0;
      let availSickLeaveBalance = 0;
      const leaveBalanceForUser = await BaseAPI.getUserLeaveBalances(Number(partyId), (getUserLeaveBalance: any) => {
        return getUserLeaveBalance?.data;
      });
      leaveBalanceForUser?.data.map((leave, index) => {
        if (leave.leaveType.leaveName === 'Planned Leave') {
          availPlannedLeaveBalance = leave.leaveBalance;
          userPlannedLeaveBalance = leave.leaveBalance;
        } else if (leave.leaveType.leaveName === 'Sick Leave') {
          availSickLeaveBalance = leave.leaveBalance;
          userSickLeaveBalance = leave.leaveBalance;
        }
      });
      const requestedSickLeaveBalance = await getRequestedLeaveBalanceByLeaveType(Number(partyId), 1);
      const requestedPlannedLeaveBalance = await getRequestedLeaveBalanceByLeaveType(Number(partyId), 2);

      const daysNeeded = Math.round(daysRequested);
      // const daysNeeded = daysRequested
      let remainingPlanned = userPlannedLeaveBalance - requestedPlannedLeaveBalance;
      let remainingSick = userSickLeaveBalance - requestedSickLeaveBalance;
      let plannedUsed = 0;
      let sickUsed = 0;
      let lopDays = 0;

      // --- Allocation Logic ---
      if (leaveType === 2) {
        // Requested Planned Leave
        if (daysNeeded <= remainingPlanned) {
          plannedUsed = daysNeeded;
        } else {
          plannedUsed = remainingPlanned > 0 ? remainingPlanned : 0;
          const remainingAfterPlanned = daysNeeded - plannedUsed;

          if (remainingAfterPlanned <= remainingSick) {
            sickUsed = remainingAfterPlanned;
          } else {
            sickUsed = remainingSick > 0 ? remainingSick : 0;
            lopDays = remainingAfterPlanned - sickUsed;
          }
        }
      } else if (leaveType === 1) {
        // Requested Sick Leave
        if (daysNeeded <= remainingSick) {
          sickUsed = daysNeeded;
        } else {
          sickUsed = remainingSick > 0 ? remainingSick : 0;
          const remainingAfterSick = daysNeeded - sickUsed;

          if (remainingAfterSick <= remainingPlanned) {
            plannedUsed = remainingAfterSick;
          } else {
            plannedUsed = remainingPlanned > 0 ? remainingPlanned : 0;
            lopDays = remainingAfterSick - plannedUsed;
          }
        }
      }
      const leaveAllocation = [
        { type: 1, days: sickUsed ? sickUsed : 0 },
        { type: 2, days: plannedUsed ? plannedUsed : 0 },
        { type: 6, days: lopDays ? lopDays : 0 }
      ];
      setSandwichLeaveAllocation(leaveAllocation);
      const allocationtext = `planned: ${plannedUsed}, Sick: ${sickUsed}, LOP:  ${lopDays}`;
      setAllocationText(allocationtext);
    }
    //  end sandwich leave borrow
  };
  // checkleavebalance end

  useEffect(() => {
    const appServerName = process.env.REACT_APP_SERVER_NAME;
    if (appServerName) {
      const isCresWorth = appServerName === 'crestworth';
      setIsCrestWorthServer(isCresWorth);
    }
  }, []);
  const fetchUserShiftCoverage = async (userId: string, date: string) => {
    try {
      const result: { data: any[] } = await new Promise((resolve, reject) => {
        BaseAPI.getShiftCoverageByUserDate(userId, date, (res: any) => {
          if (res.status === 200) {
            resolve(res);
          } else {
            reject(new Error(res.message || 'Failed to fetch shift coverage'));
          }
        });
      });
      return result?.data;
    } catch (err) {
      console.error('Error fetching shift coverage data:', err);
      return [];
    }
  };

  const updateShiftCoverage = async (shift) => {
    try {
      const currentDate = new Date();
      const payload = {
        date: shift?.date,
        shift: 'ending'
      };
      const response = await BaseAPI.updateShiftCoverage(shift?.id, payload);
    } catch (error) {
      console.error('Unexpected error during update:', error);
    }
  };

  const handleSubmit = async () => {
    setPermissionDialogOpen(false);
    const data = JSON.parse(sessionStorage.getItem('UserpartyId'));
    const userEmaildata = JSON.parse(sessionStorage.getItem('UserEmail'));

    const confirm = await Swal.fire({
      title: 'Confirm Permission ',
      html: `Are you sure you want to apply <strong>${permissionHours}</strong>  for ${permissionType} on <strong>today</strong> ?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4caf50',
      cancelButtonColor: '#35cce6',
      confirmButtonText: 'Yes',
      cancelButtonText: 'Cancel'
    });

    if (confirm.isConfirmed) {
      try {
        const payload = {
          date: dayjs().format('DD-MM-YYYY'),
          permissionType: permissionType,
          startTime: permissionStart,
          resumingTime: permissionResume,
          endTime: permissionEnd,
          noOfHours: permissionHours,
          reason: permissionReason,
          party: data,
          email: userEmaildata,
          supervisorEmailId: employeeSupervisorEmail,
          isContinueWFH: permissionType === 'Continue WFH' ? true : false,
          // CRITICAL FIX: Skip Mattermost notification for RDS servers
          skipMattermostNotification: tenantFeatures.allowSupervisorMarkingAttendance || false
        };

        await BaseAPI.createPermissionRequest(payload, async (result: any) => {
          if (result.status === 200 || result.status === 201) {
            if (permissionType === 'Continue WFH' || permissionType === 'Leaving Early') {
              const currentDate = formatDateOnly(new Date());
              const userTodayShift = await fetchUserShiftCoverage(data, currentDate);
              if (userTodayShift && userTodayShift[0]) {
                await updateShiftCoverage(userTodayShift[0]);
              }
            }

            Swal.fire({
              title: 'Success',
              text: 'Permission submitted successfully.',
              icon: 'success',
              confirmButtonColor: '#4caf50'
            });

            // Reset form
            setPermissionType('');
            setPermissionStart('');
            setPermissionResume('');
            setPermissionEnd('');
            setPermissionHours('');
            setPermissionReason('');
          } else {
            // Handle specific error for Mattermost failures gracefully
            if (result.message && result.message.includes('Mattermost')) {
              console.warn('⚠️ Mattermost notification failed, but permission was created:', result.message);

              // Still show success to user since permission was created
              Swal.fire({
                title: 'Permission Created',
                text: 'Permission submitted successfully. (Notification system unavailable)',
                icon: 'success',
                confirmButtonColor: '#4caf50'
              });

              // Reset form
              setPermissionType('');
              setPermissionStart('');
              setPermissionResume('');
              setPermissionEnd('');
              setPermissionHours('');
              setPermissionReason('');
            } else {
              Swal.fire('Error', result.message || 'Failed to submit permission request.', 'error');
            }
          }
        });
      } catch (error) {
        console.error('Submission error:', error);

        // Check if it's a Mattermost-related error
        const errorMessage = error?.message || error?.toString() || '';
        if (errorMessage.includes('Mattermost') || errorMessage.includes('notify')) {
          console.warn('⚠️ Mattermost notification failed, checking if permission was created...');

          // Show a more graceful message
          Swal.fire({
            title: 'Permission May Be Created',
            text: 'There was an issue with the notification system. Please refresh and check if your permission was recorded.',
            icon: 'warning',
            confirmButtonColor: '#4caf50'
          });
        } else {
          Swal.fire('Error', 'Something went wrong during submission.', 'error');
        }
      }
    }
  };

  const updatePermissioById = async () => {
    setPermissionDialogOpen(false);

    try {
      const payload = {
        startTime: permissionStart,
        endTime: permissionEnd,
        resumingTime: permissionResume,
        noOfHours: permissionHours,
        reason: permissionReason
      };

      const response = await BaseAPI.updatePermissioById(Number(permissionId), payload);
      if (response.status === 200) {
        Swal.fire({
          title: 'Updated',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#4caf50'
        });
        setTimeout(() => {
          // navigate(`/dashboard`);
        }, 2000);
      } else {
        console.error('Update failed:', response);
      }
    } catch (error) {
      console.error('Unexpected error during update:', error);
    }
  };

  return (
    <>
      {!isCrestWorthServer && (
        <>
          <Tooltip arrow title="Quick Link">
            <IconButton color="primary" onClick={handleQuickLinkClick}>
              <img src="/static/images/logo/quick_link.jpg" alt="Quick Link" style={{ width: 24, height: 24 }} />
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={quickLinkAnchorEl}
            open={Boolean(quickLinkAnchorEl)}
            onClose={handleQuickLinkClose}
            PaperProps={{
              style: {
                minWidth: 200
              }
            }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                Quick Link
              </Typography>
            </Box>
            {tenantFeatures.quickLinksWFHEnabled && (
              <MenuItem onClick={openWFHDialog}>
                <HomeWorkIcon fontSize="medium" sx={{ mr: 1 }} />
                Work From Home
              </MenuItem>
            )}
            {tenantFeatures.quickLinksPermissionEnabled && (
              <MenuItem onClick={openPermissionDialog}>
                <AccessTimeIcon fontSize="medium" sx={{ mr: 1 }} />
                Permission
              </MenuItem>
            )}
            {tenantFeatures.quickLinksEmergencyEnabled && (
              <MenuItem onClick={OpenEmergencyDialog}>
                <MedicalServicesIcon fontSize="medium" sx={{ mr: 1 }} />
                Emergency/Sick Leave
              </MenuItem>
            )}
          </Menu>

          <Tooltip arrow title="Notifications">
            <IconButton color="primary" ref={ref} onClick={handleOpen}>
              <NotificationsBadge
                badgeContent={unreadCount}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
              >
                <NotificationsActiveTwoToneIcon />
              </NotificationsBadge>
            </IconButton>
          </Tooltip>
        </>
      )}
      {category == 'Settings' ? (
        <Tooltip arrow title="Settings">
          <IconButton color="primary" ref={ref} aria-describedby={id} onClick={handleClick}>
            <NotificationsBadge
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right'
              }}
            >
              <SettingsOutlinedIcon />
            </NotificationsBadge>
          </IconButton>
        </Tooltip>
      ) : (
        <></>
      )}

      <Popover
        sx={{ top: '70px', marginRight: '10px', backdropFilter: 'blur(0px)' }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={isOpen}
      >
        <Box sx={{ p: 2 }} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5">Notifications</Typography>
          <Button onClick={navigateToNotificationPage} variant="text" size="small">
            View All
          </Button>
        </Box>
        <Divider />
        <List sx={{ p: 0 }}>
          {notifications.length > 0 ? (
            notifications.slice(0, 5).map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  p: 2,
                  minWidth: 350,
                  display: { xs: 'block', sm: 'flex' },
                  backgroundColor: notification.isRead ? 'inherit' : alpha('#f5f5f5', 0.3),
                  '&:hover': {
                    backgroundColor: alpha('#f0f0f0', 0.5),
                    cursor: 'pointer'
                  }
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                <Box flex="1">
                  <Box display="flex" alignItems="center">
                    {notification.type === 'warning' && <WarningAmberIcon color="warning" sx={{ mr: 1 }} />}
                    <Box>
                      <Typography sx={{ fontWeight: 'bold' }}>{notification.title}</Typography>
                      <Typography variant="caption" sx={{ textTransform: 'none', display: 'block', mb: 1 }}>
                        {formatNotificationDate(notification.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography component="span" variant="body2" color="text.secondary">
                    {notification.message}
                  </Typography>
                </Box>
              </ListItem>
            ))
          ) : (
            <ListItem sx={{ p: 2, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
              <Box flex="1">
                <Typography component="span" variant="body2" color="text.secondary">
                  No new notifications
                </Typography>
              </Box>
            </ListItem>
          )}
        </List>
      </Popover>
      <Popover
        sx={{ top: '70px', marginRight: '10px', backdropFilter: 'blur(0px)' }}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disablePortal={false}
      >
        <Box sx={{ p: 2 }} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h3">Settings</Typography>
        </Box>
        <Divider />
        <List sx={{ p: 0 }}>
          <ListItem sx={{ p: 1, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
            <Box flex="1" sx={{ display: 'contents' }}>
              <CorporateFareOutlinedIcon />
              <Box display="flex" justifyContent="space-between">
                <Button sx={{ color: '#5c5454' }} onClick={() => OrganisationProfile()}>
                  Organisation Profile
                </Button>
              </Box>
            </Box>
          </ListItem>
        </List>
        <List sx={{ p: 0 }}>
          <ListItem sx={{ p: 1, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
            <Box flex="1" sx={{ display: 'contents' }}>
              <LocationOnOutlinedIcon />
              <Box display="flex" justifyContent="space-between">
                <Button sx={{ color: '#5c5454' }} onClick={() => WorkLocations()}>
                  Work Locations
                </Button>
              </Box>
            </Box>
          </ListItem>
        </List>
        <List sx={{ p: 0 }}>
          <ListItem sx={{ p: 1, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
            <Box flex="1" sx={{ display: 'contents' }}>
              <LanOutlinedIcon />
              <Box display="flex" justifyContent="space-between">
                <Button sx={{ color: '#5c5454' }} onClick={() => Departments()}>
                  Departments{' '}
                </Button>
              </Box>
            </Box>
          </ListItem>
        </List>
        <List sx={{ p: 0 }}>
          <ListItem sx={{ p: 1, minWidth: 350, display: { xs: 'block', sm: 'flex' } }}>
            <Box flex="1" sx={{ display: 'contents' }}>
              <AssignmentIndOutlinedIcon />
              <Box display="flex" justifyContent="space-between">
                <Button sx={{ color: '#5c5454' }} onClick={() => Designation()}>
                  Designation{' '}
                </Button>
              </Box>
            </Box>
          </ListItem>
        </List>
      </Popover>

      <Dialog open={wfhDialogOpen} onClose={() => setWfhDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#33C2FF', color: 'white' }}>Apply Work From Home</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Date"
            value={new Date().toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}
            margin="normal"
            InputProps={{
              readOnly: true
            }}
            disabled
          />

          {/* <Typography variant="subtitle2" gutterBottom>Select WFH Type</Typography> */}
          <Box sx={{ display: 'flex', flexDirection: 'row', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    color: '#57CA22',
                    '&.Mui-checked': {
                      color: '#57CA22'
                    },
                    '&.Mui-disabled': {
                      color: 'gray'
                    }
                  }}
                  checked={firstHalf}
                  onChange={(e) => setFirstHalf(e.target.checked)}
                  disabled={existingHalfStatus === 'FULL' || existingHalfStatus === 'FIRST'}
                />
              }
              label="1st Half"
              sx={{ mr: 4 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  sx={{
                    color: '#57CA22',
                    '&.Mui-checked': {
                      color: '#57CA22'
                    },
                    '&.Mui-disabled': {
                      color: 'gray'
                    }
                  }}
                  checked={secondHalf}
                  onChange={(e) => setSecondHalf(e.target.checked)}
                  disabled={existingHalfStatus === 'FULL' || existingHalfStatus === 'SECOND'}
                />
              }
              label="2nd Half"
            />
          </Box>

          <TextField
            fullWidth
            label="Reason"
            placeholder="Enter reason for WFH..."
            multiline
            required
            rows={3}
            value={wfhReason}
            onChange={(e) => setWfhReason(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setWfhDialogOpen(false)} color="info" variant="contained">
            Cancel
          </Button>
          <Button
            onClick={submitWFHRequest}
            color="success"
            variant="contained"
            disabled={(!firstHalf && !secondHalf) || !wfhReason.trim()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={permissionDialogOpen} onClose={() => setPermissionDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#33C2FF', color: 'white' }}>Apply Permission</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center">
                  <CalendarTodayIcon sx={{ fontSize: 18, color: 'gray', mr: 1 }} />
                  <Typography variant="body2">{dayjs().format('DD-MMM-YYYY')}</Typography>
                </Box>

                <Box display="flex" alignItems="center">
                  <AccessTimeIcon sx={{ fontSize: 18, color: 'gray', mr: 1 }} />
                  <Typography variant="body2">
                    <strong>{shiftTime[0]?.shiftType || 'N/A'}</strong> – {formatTime(shiftTime[0]?.startTime)} to{' '}
                    {formatTime(shiftTime[0]?.endTime)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={permissionType}
                  onChange={async (e) => {
                    const selectedType = e.target.value;
                    setPermissionType(selectedType);
                    setPermissionDialogOpen(true);
                    setPermissionHoursError(false);
                    setPermissionHoursHelperText('');

                    setPermissionStart('');
                    setPermissionResume('');
                    setPermissionEnd('');
                    setPermissionReason('');

                    const existing = await getPermissioByEmployeeIdData(selectedType);
                    if (existing) {
                      setIsUpdatePermissionMode(true);
                      setPermissionStart(existing.startTime);
                      setPermissionEnd(existing.endTime);
                      setPermissionResume(existing.resumingTime);
                      setPermissionReason(existing.reason);
                      const start = dayjs(`2023-01-01T${existing.startTime}`);
                      const end = dayjs(`2023-01-01T${existing.endTime}`);
                      if (end.isAfter(start)) {
                        const diff = end.diff(start, 'minute');
                        const hrs = Math.floor(diff / 60);
                        const mins = diff % 60;
                        const formatted = `${hrs > 0 ? `${hrs} hr ` : ''}${mins > 0 ? `${mins} min` : ''}`.trim();
                        setPermissionHours(formatted);
                      }
                      setPermissionId(existing.id);
                    } else {
                      setIsUpdatePermissionMode(false);
                      setPermissionHours('');
                    }
                  }}
                >
                  <FormControlLabel
                    value="Late Coming"
                    control={
                      <Radio
                        sx={{
                          color: '#57CA22',
                          '&.Mui-checked': {
                            color: '#57CA22'
                          }
                        }}
                      />
                    }
                    label="Late Coming"
                  />
                  <FormControlLabel
                    value="Leaving Early"
                    control={
                      <Radio
                        sx={{
                          color: '#57CA22',
                          '&.Mui-checked': {
                            color: '#57CA22'
                          }
                        }}
                      />
                    }
                    label="Leaving Early"
                  />
                  <FormControlLabel
                    value="Permission"
                    control={
                      <Radio
                        sx={{
                          color: '#57CA22',
                          '&.Mui-checked': {
                            color: '#57CA22'
                          }
                        }}
                      />
                    }
                    label="During Work hours"
                  />
                  {tenantFeatures.quickLinksWFHEnabled && (
                    <FormControlLabel
                      value="Continue WFH"
                      control={
                        <Radio
                          sx={{
                            color: '#57CA22',
                            '&.Mui-checked': {
                              color: '#57CA22'
                            }
                          }}
                        />
                      }
                      label="Continue Work From Home"
                    />
                  )}
                </RadioGroup>
              </FormControl>
            </Grid>

            {permissionType !== 'Continue WFH' ? (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={
                      permissionType === 'Leaving Early' ? 'Leaving Time' : permissionType === 'Continue WFH' ? 'Start Time' : 'From Time'
                    }
                    type="time"
                    value={permissionStart}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      readOnly: permissionType === 'Late Coming' || permissionType === 'Continue WFH'
                    }}
                    onChange={(e) => setPermissionStart(e.target.value)}
                    disabled={permissionType === 'Late Coming' || permissionType === 'Continue WFH'}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={permissionType === 'Late Coming' ? 'Reaching Time' : permissionType === 'Continue WFH' ? 'End Time' : 'To Time'}
                    type="time"
                    value={permissionEnd}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      readOnly: permissionType === 'Leaving Early'
                    }}
                    onChange={(e) => setPermissionEnd(e.target.value)}
                    disabled={permissionType === 'Leaving Early' || isLateLoginFreeze}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="No. of Hours"
                    type="text"
                    disabled
                    value={permissionHours}
                    InputProps={{ readOnly: true }}
                    error={permissionHoursError}
                    helperText={permissionHoursHelperText}
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={12}>
                  <TextField
                    fullWidth
                    label="Resuming Time"
                    type="time"
                    value={permissionResume}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => setPermissionResume(e.target.value)}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Reason"
                value={permissionReason}
                placeholder="Enter reason for permission..."
                onChange={(e) => setPermissionReason(e.target.value)}
                InputProps={{
                  readOnly: permissionType !== 'Permission'
                }}
                disabled={permissionType !== 'Permission'}
              />
            </Grid>
            {isLateLoginFreeze && (
              <Typography sx={{ fontSize: '13px', color: '#ff0000', fontWeight: 500, marginLeft: '30px', marginTop: '10px' }}>
                Cannot apply permission after shift time !
              </Typography>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button
            onClick={() => {
              setPermissionDialogOpen(false);
              setIsUpdatePermissionMode(false);
            }}
            color="info"
            variant="contained"
          >
            Cancel
          </Button>
          {permissionType !== 'Continue WFH' ? (
            <>
              <Button
                onClick={isUpdatePermissionMode ? updatePermissioById : handleSubmit}
                disabled={
                  !permissionType ||
                  !permissionStart ||
                  !permissionEnd ||
                  !permissionHours ||
                  !permissionReason.trim() ||
                  permissionHoursError ||
                  isLateLoginFreeze
                }
                color="success"
                variant="contained"
              >
                {isUpdatePermissionMode ? 'Update' : 'Submit'}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={isUpdatePermissionMode ? updatePermissioById : handleSubmit}
                disabled={!permissionType || !permissionResume || !permissionReason.trim()}
                color="success"
                variant="contained"
              >
                {isUpdatePermissionMode ? 'Update' : 'Submit'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={emergencyDialogOpen} onClose={() => setEmergencyDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#33C2FF', color: 'white' }}>Apply Emergency/Sick Leave</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <CalendarTodayIcon sx={{ fontSize: 18, color: 'gray', mr: 1 }} />
                <Typography variant="body2">{dayjs().format('DD-MMM-YYYY')}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="emergency-reason-label">Emergency Reason</InputLabel>
                <Select
                  labelId="emergency-reason-label"
                  value={emergencyReason}
                  label="Emergency Reason"
                  onChange={(e) => {
                    const value = e.target.value;
                    setEmergencyReason(value);
                    fetchUserLeaveBalance(sickLeaveId);
                  }}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#57CA22'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#57CA22'
                    }
                  }}
                >
                  <MenuItem value="Emergency Travel">Emergency Travel </MenuItem>
                  <MenuItem value="I am not well ">I am not well </MenuItem>
                  <MenuItem value="My family members are not well ">My family members are not well </MenuItem>
                </Select>
              </FormControl>
              {isSandwichLeave && (
                <Grid item xs={12} sx={{ fontSize: '13px', color: '#af1e1e', fontWeight: 500, padding: '10px' }}>
                  <Typography variant="body2" color="text.danger">
                    <strong>This leave fall under the sandwich policy</strong>
                  </Typography>
                  <Typography variant="body2" color="text.danger" sx={{ fontSize: '13px' }}>
                    {allocationText}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {emergencyReason && (
              <>
                <Grid item xs={5}>
                  <Typography variant="body2" color="text.secondary">
                    Leave Type : <strong>Sick</strong>
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Balance : {userLeaveBalancesCount}
                  </Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2" color="text.secondary">
                    Requested : {requestedLeaveCount}
                  </Typography>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={firstHalfEmergency}
                      onChange={(e) => {
                        setFirstHalfEmergency(e.target.checked);
                      }}
                      sx={{
                        color: '#57CA22',
                        '&.Mui-checked': {
                          color: '#57CA22'
                        },
                        '&.Mui-disabled': {
                          color: 'gray'
                        }
                      }}
                      disabled={existingHalfStatus === 'FULL' || existingHalfStatus === 'FIRST'}
                    />
                  }
                  label="1st Half"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={secondHalfEmergency}
                      onChange={(e) => {
                        setSecondHalfEmergency(e.target.checked);
                      }}
                      sx={{
                        color: '#57CA22',
                        '&.Mui-checked': {
                          color: '#57CA22'
                        },
                        '&.Mui-disabled': {
                          color: 'gray'
                        }
                      }}
                      disabled={existingHalfStatus === 'FULL' || existingHalfStatus === 'SECOND'}
                    />
                  }
                  label="2nd Half"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center' }}>
          <Button onClick={() => setEmergencyDialogOpen(false)} color="info" variant="contained">
            Cancel
          </Button>
          <Button onClick={submitEmergencyLeaveRequest} disabled={!emergencyReason} color="success" variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default HeaderNotifications;
